import { useState } from "react";
import { FileCheck, Calendar, CheckCircle, Building2 } from "lucide-react";
import { formatCurrency } from "../../lib/formatters";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

/**
 * ContractReceivedDialog - Dialog for marking a contract as received
 * Allows selecting which GC the contract is with and which bid sheets were accepted
 */
const ContractReceivedDialog = ({ 
  open, 
  onOpenChange, 
  job, 
  onContractReceived,
  generalContractors = [],
}) => {
  const [receivedDate, setReceivedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectedBidIds, setSelectedBidIds] = useState([]);
  const [selectedGcId, setSelectedGcId] = useState("");
  const [contractNumber, setContractNumber] = useState("");
  const [projectNumber, setProjectNumber] = useState("");
  const [billingDate, setBillingDate] = useState("");

  const biddingGCs = job?.bidding_gcs || [];

  // Get bid sheets (exclude change orders)
  const bidSheets = (job?.bid_sheets || []).filter(
    bs => bs.bid_type !== "change_order" && !bs.is_change_order
  );

  const baseBid = bidSheets.find(bs => bs.bid_type === "base_bid" || !bs.bid_type);
  const alternates = bidSheets.filter(bs => bs.bid_type === "alternate");

  // Calculate total from selected bids
  const selectedTotal = bidSheets
    .filter(bs => selectedBidIds.includes(bs.id))
    .reduce((sum, bs) => sum + (bs.grand_total || 0), 0);

  const handleToggleBid = (bidId) => {
    setSelectedBidIds(prev => 
      prev.includes(bidId) 
        ? prev.filter(id => id !== bidId)
        : [...prev, bidId]
    );
  };

  const handleConfirm = () => {
    if (!receivedDate || selectedBidIds.length === 0) return;
    // GC is required if there are bidding GCs
    if (biddingGCs.length > 0 && !selectedGcId) return;
    
    onContractReceived({
      receivedDate,
      acceptedBidIds: selectedBidIds,
      contractAmount: Math.round(selectedTotal),
      contractedGcId: selectedGcId || null,
      contractNumber: contractNumber.trim() || null,
      projectNumber: projectNumber.trim() || null,
      billingDate: billingDate || null,
    });
    
    // Reset state
    setReceivedDate(new Date().toISOString().split("T")[0]);
    setSelectedBidIds([]);
    setSelectedGcId("");
    setContractNumber("");
    setProjectNumber("");
    setBillingDate("");
  };

  const handleClose = () => {
    setReceivedDate(new Date().toISOString().split("T")[0]);
    setSelectedBidIds([]);
    setSelectedGcId("");
    setContractNumber("");
    setProjectNumber("");
    setBillingDate("");
    onOpenChange(false);
  };

  // Determine if confirm button should be disabled
  const gcRequired = biddingGCs.length > 0;
  const isDisabled = !receivedDate || selectedBidIds.length === 0 || (gcRequired && !selectedGcId);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCheck className="w-5 h-5" />
            Contract Received
          </DialogTitle>
          <DialogDescription>
            Select which GC the contract is with, which bids were accepted, and the contract date.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* GC Selection - Only if there are bidding GCs */}
          {biddingGCs.length > 0 && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Contract With (GC)
              </Label>
              <Select value={selectedGcId} onValueChange={setSelectedGcId}>
                <SelectTrigger data-testid="contract-gc-select">
                  <SelectValue placeholder="Select the GC awarding the contract..." />
                </SelectTrigger>
                <SelectContent>
                  {biddingGCs
                    .filter((gc) => !gc.bid_not_sent)
                    .map((gc) => (
                      <SelectItem key={gc.gc_id} value={gc.gc_id}>
                        {gc.gc_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Date Input */}
          <div className="space-y-2">
            <Label htmlFor="contract-date" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Contract Received Date
            </Label>
            <Input
              id="contract-date"
              type="date"
              value={receivedDate}
              onChange={(e) => setReceivedDate(e.target.value)}
              data-testid="contract-received-date"
            />
          </div>

          {/* Contract Details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contract-number">Contract #</Label>
              <Input
                id="contract-number"
                value={contractNumber}
                onChange={(e) => setContractNumber(e.target.value)}
                placeholder="e.g., CNT-2026-001"
                data-testid="contract-number-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="project-number">Project #</Label>
              <Input
                id="project-number"
                value={projectNumber}
                onChange={(e) => setProjectNumber(e.target.value)}
                placeholder="e.g., P-2026-001"
                data-testid="project-number-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="billing-day">Billing Day of Month</Label>
            <Select value={billingDate} onValueChange={setBillingDate}>
              <SelectTrigger data-testid="billing-day-select">
                <SelectValue placeholder="Select billing day..." />
              </SelectTrigger>
              <SelectContent>
                {[...Array(31)].map((_, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>
                    {i + 1}{i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'} of each month
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Bid Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Select Accepted Bids</Label>
            
            {baseBid && (
              <BidCheckboxItem
                bid={baseBid}
                isSelected={selectedBidIds.includes(baseBid.id)}
                onToggle={() => handleToggleBid(baseBid.id)}
                label="Base Bid"
              />
            )}

            {alternates.length > 0 && (
              <div className="space-y-2 mt-3">
                <Label className="text-xs text-muted-foreground uppercase">Alternates</Label>
                {alternates.map(alt => (
                  <BidCheckboxItem
                    key={alt.id}
                    bid={alt}
                    isSelected={selectedBidIds.includes(alt.id)}
                    onToggle={() => handleToggleBid(alt.id)}
                  />
                ))}
              </div>
            )}

            {bidSheets.length === 0 && (
              <p className="text-sm text-muted-foreground italic">
                No bid sheets found. Create a bid sheet in the Estimating tab first.
              </p>
            )}
          </div>

          {/* Selected Total */}
          {selectedBidIds.length > 0 && (
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Contract Amount:</span>
                <span className="text-xl font-bold text-primary">
                  {formatCurrency(selectedTotal)}
                </span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={isDisabled}
            data-testid="confirm-contract-received-btn"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Confirm Contract
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Individual bid checkbox item
const BidCheckboxItem = ({ bid, isSelected, onToggle, label }) => {
  return (
    <div 
      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
        isSelected ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
      }`}
      onClick={onToggle}
      data-testid={`bid-checkbox-${bid.id}`}
    >
      <div className="flex items-center gap-3">
        <Checkbox 
          checked={isSelected} 
          onCheckedChange={onToggle}
          onClick={(e) => e.stopPropagation()}
        />
        <div>
          <p className="font-medium text-sm">{label || bid.name}</p>
          {bid.description && (
            <p className="text-xs text-muted-foreground">{bid.description}</p>
          )}
        </div>
      </div>
      <span className="font-semibold">{formatCurrency(bid.grand_total || 0)}</span>
    </div>
  );
};

export default ContractReceivedDialog;
